import { useBooleanState } from '@cian/react-utils';
import { Responsive } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { DesktopSearchBlockLayout } from 'shared/components/DesktopSearchBlockLayout/DesktopSearchBlock';
import { MobileSearchBlock } from 'shared/components/MobileSearchBlock';
import { RedesignMainpageSearchBox } from 'shared/components/RedesignMainpageSearchBox';
import { getMainpageFiltersUrl } from 'shared/constants/constants';
import { selectCurrentRegion } from 'shared/selectors/regions';
import { EPageType } from 'shared/types/pageType';

import { useApplicationContext } from '../ApplicationContext';
import { DesktopSearchInputContainer } from '../DesktopSearchInputContainer';
import { SearchBlockModalContainer } from '../SearchBlockModalContainer';
import { SearchTipsContainer } from '../SearchTipsContainer/SearchTipsContainer';
import { trackSearchStringClick } from './tracking';

export const SearchBlockContainer: React.FC = () => {
  const { custom } = useApplicationContext();
  const currentRegionId = useSelector(selectCurrentRegion);

  const { state: isModalOpen, setTrue: setModalOpen, setFalse: setModalClose } = useBooleanState(false);

  const isMainpage = custom.pageType === EPageType.MAIN;
  const filtersRegionId = currentRegionId || custom.userLocationId;
  const mainpageFiltersUrl = getMainpageFiltersUrl(filtersRegionId);

  const handleSearchButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setModalOpen();
    },
    [setModalOpen],
  );

  const handleRedesignSearchButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      trackSearchStringClick(false);
      event.preventDefault();
      setModalOpen();
    },
    [setModalOpen],
  );

  const handleFiltersButtonClick = React.useCallback(() => {
    trackSearchStringClick(true);
  }, []);

  const renderMobileSearchBlock = (): React.ReactNode => {
    if (!isMainpage) {
      return <MobileSearchBlock onSearchClick={handleSearchButtonClick} isMainpage={false} />;
    }

    if (custom.isRedesignedMainPage) {
      return (
        <RedesignMainpageSearchBox
          filtersUrl={mainpageFiltersUrl}
          onSearchClick={handleRedesignSearchButtonClick}
          onFiltersClick={handleFiltersButtonClick}
        />
      );
    }

    return <MobileSearchBlock onSearchClick={handleSearchButtonClick} isMainpage />;
  };

  return (
    <>
      <Responsive desktop>
        <DesktopSearchBlockLayout>
          <DesktopSearchInputContainer />
          <SearchTipsContainer isMainPageTips={true} desktopStyle={true} onClick={() => {}} />
        </DesktopSearchBlockLayout>
      </Responsive>
      <Responsive phone tablet>
        {renderMobileSearchBlock()}
        <SearchBlockModalContainer open={isModalOpen} onClose={setModalClose} />
      </Responsive>
    </>
  );
};

SearchBlockContainer.displayName = 'SearchBlockContainer';
